import { useRef, useCallback, useEffect, useState } from 'react'
import { KEYBOARD_KEYS_MAPPING } from 'consts/events'
import { checkIsTargetEditable, checkMetaKeys } from 'utils'

export const useVerticalResizeHandle = ({
  minHeight = 100,
  maxHeight = 480
}: {
  minHeight?: number
  maxHeight?: number
}) => {
  const ref = useRef<HTMLDivElement | null>(null)

  const setBodyCursor = (cursor: string) => {
    document.body.style.cursor = cursor
  }

  const onMouseDown = useCallback(
    (event: MouseEvent | TouchEvent) => {
      event.preventDefault()
      const startY =
        'clientY' in event ? event.clientY : event.touches[0].clientY
      const startHeight = ref.current?.clientHeight ?? 0

      setBodyCursor('row-resize')

      const onMouseMove = (e: MouseEvent | TouchEvent) => {
        e.preventDefault()
        e.stopPropagation()
        if (!ref.current) return
        const clientY = 'clientY' in e ? e.clientY : e.touches[0].clientY

        const newHeight = startHeight + (startY - clientY)

        if (newHeight >= minHeight && newHeight <= maxHeight) {
          ref.current.style.height = `${newHeight}px`
        }
      }

      const onMouseUp = () => {
        setBodyCursor('')
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('touchmove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
        document.removeEventListener('touchend', onMouseUp)
      }

      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
      document.addEventListener('touchmove', onMouseMove, { passive: false })
      document.addEventListener('touchend', onMouseUp, { passive: false })
    },
    [minHeight, maxHeight]
  )

  return {
    ref,
    onMouseDown
  }
}

interface IKeyPressOptions {
  configs?: { [key: string]: boolean }
  targetAreaSelector?: string
}

export const useKeyPressWithConfigs = (
  targetKey: keyof typeof KEYBOARD_KEYS_MAPPING,
  handler: (event: KeyboardEvent) => void,
  options?: IKeyPressOptions
) => {
  const { configs, targetAreaSelector } = options || {}
  const [isMouseInArea, setIsMouseInArea] = useState(!targetAreaSelector)

  useEffect(() => {
    if (!targetAreaSelector) return
    const element = document.querySelector(targetAreaSelector) as HTMLElement
    if (!element) return

    const handleEnter = () => setIsMouseInArea(true)
    const handleLeave = () => setIsMouseInArea(false)

    element.addEventListener('mouseenter', handleEnter)
    element.addEventListener('mouseleave', handleLeave)
    return () => {
      element.removeEventListener('mouseenter', handleEnter)
      element.removeEventListener('mouseleave', handleLeave)
    }
  }, [targetAreaSelector])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { charCode, keyCode, shiftKey, ctrlKey, metaKey } = event
      if (checkIsTargetEditable(event) || !isMouseInArea) return

      if (
        (charCode || keyCode) === KEYBOARD_KEYS_MAPPING[targetKey] &&
        checkMetaKeys({
          configs,
          eventKeys: { shiftKey, ctrlKey, metaKey }
        })
      ) {
        event.preventDefault()
        handler(event)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handler, targetKey, configs, isMouseInArea])
}

export function useEventListener<T extends Event = Event>(
  eventName: string,
  handler: (e: T) => unknown,
  element?: EventTarget | Document | null,
  options?: boolean | AddEventListenerOptions
): void {
  // Create a ref that stores handler
  const savedHandler = useRef<(event: T) => void>(handler)

  // Update ref.current value if handler changes.
  // This allows our effect below to always get latest handler ...
  // ... without us needing to pass it in effect deps array ...
  // ... and potentially cause effect to re-run every render.
  useEffect(() => {
    savedHandler.current = handler
  }, [handler])

  useEffect(() => {
    // Make sure element supports addEventListener
    if (!element || !element.addEventListener) return

    // Create event listener that calls handler function stored in ref
    const eventListener = (event: T) => savedHandler.current(event)

    // Add event listener
    element.addEventListener(eventName, eventListener as EventListener, options)

    // Remove event listener on cleanup
    return () => {
      element.removeEventListener(
        eventName,
        eventListener as EventListener,
        options
      )
    }
  }, [eventName, element, options]) // Removed savedHandler from deps as refs don't trigger re-renders
}

export const useDeviceHeight = (size: number): boolean => {
  const getSize = (): { width: number; height: number } => {
    return {
      width: window.innerWidth,
      height: window.innerHeight
    }
  }

  const [windowSize, setWindowSize] = useState(getSize)

  function handleResize() {
    setWindowSize(getSize())
  }

  useEventListener('resize', handleResize)

  if (windowSize.height) {
    return windowSize.height <= size
  }

  return false
}

export const useHover = (
  elements: Array<HTMLElement | SVGElement | null> | null
): boolean => {
  const [isHovered, setIsHovered] = useState(false)
  useEffect(() => {
    if (!elements?.length) return

    const handleMouseEnter = () => {
      setIsHovered(true)
    }

    const handleMouseLeave = (event: Event) => {
      // Check if we're moving to another ref in the array
      const mouseEvent = event as MouseEvent
      const toElement = mouseEvent.relatedTarget as HTMLElement
      if (toElement && elements.some(element => element?.contains(toElement))) {
        return // Still hovering over one of the refs
      }

      setIsHovered(false)
    }

    const cleanupFunctions: (() => void)[] = []

    elements.forEach(element => {
      if (!element) return

      element.addEventListener('mouseenter', handleMouseEnter)
      element.addEventListener('mouseleave', handleMouseLeave)

      cleanupFunctions.push(() => {
        element.removeEventListener('mouseenter', handleMouseEnter)
        element.removeEventListener('mouseleave', handleMouseLeave)
      })
    })

    return () => {
      cleanupFunctions.forEach(cleanup => cleanup())
    }
  }, [elements])

  return isHovered
}
